/* eslint-disable max-classes-per-file */
import { openToastr } from '@Common/handlers/services';
import { Theme } from '@quillbot/design-system';
import { capitalize, safeJSONParse } from 'core/utils';
import { GenerateCitationsResponse } from 'src/researcher/types/api';
import { getCitationStyles } from 'src/researcher/handlers/api';
import { useSafeDispatch } from '@Common/hooks';
import { RootState } from '@Common/models/redux/store';
import QuillLocalStorage from 'core/utils/localStorageClient';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { getReduxStore } from '@Common/redux/store';
import {
  Contributor,
  CitationStylesEnum,
  CitationSourceEnum,
  ContributorDetails,
  ContributorTypes,
  IPublicationInfo,
  CitedData,
  ICitationStyle,
} from '../types';
import logger from '../../../core/logger/logger';
import { ObjectTypeForExport } from '../constants';
import { setCitationStyles } from '../reducers/actionCreators';

export enum AlignmentType {
  START = 'start',
  END = 'end',
  CENTER = 'center',
  BOTH = 'both',
  JUSTIFIED = 'both',
  DISTRIBUTE = 'distribute',
  LEFT = 'left',
  RIGHT = 'right',
}

export class CitationTextRun {
  text;

  size;

  italics;

  bold;

  subScript;

  superScript;

  font;

  type;

  breakValue;

  constructor({
    text,
    size,
    italics,
    bold,
    subScript,
    superScript,
    font,
    breakValue,
  }: {
    text: string | undefined;
    size: number;
    italics?: boolean;
    bold?: boolean;
    subScript?: boolean;
    superScript?: boolean;
    font: string;
    breakValue?: number;
  }) {
    this.text = text;
    this.size = size;
    if (italics !== undefined) {
      this.italics = italics;
    }
    if (breakValue !== undefined) {
      this.breakValue = breakValue;
    }
    if (bold !== undefined) {
      this.bold = bold;
    }
    if (subScript !== undefined) {
      this.subScript = subScript;
    }
    if (superScript !== undefined) {
      this.superScript = superScript;
    }
    this.font = font;
    this.type = ObjectTypeForExport.TextRun;
  }
}

export class CitationParagraph {
  indent;

  spacing;

  children;

  type;

  alignment;

  constructor({
    indent,
    spacing,
    children,
    alignment,
  }: {
    indent?: { hanging: number; start: number };
    spacing: { line: number };
    children: CitationTextRun[];
    alignment?: AlignmentType;
  }) {
    if (indent !== undefined) {
      this.indent = indent;
    }
    this.spacing = spacing;
    this.children = children;
    if (alignment !== undefined) {
      this.alignment = alignment;
    }
    this.type = ObjectTypeForExport.Paragraph;
  }
}

export class CitationDocument {
  title;

  sections;

  constructor({
    title,
    sections,
  }: {
    title: string;
    sections: { children: CitationParagraph[] }[];
  }) {
    this.title = title;
    this.sections = sections;
  }
}

export interface IContributors {
  [ContributorTypes.AUTHORS]: ContributorDetails[];
  [ContributorTypes.COMPILERS]: ContributorDetails[];
  [ContributorTypes.EDITORS]: ContributorDetails[];
  [ContributorTypes.TRANSLATORS]: ContributorDetails[];
  [ContributorTypes.ILLUSTRATORS]: ContributorDetails[];
}
export interface ICitationData {
  contributors: IContributors;
  publicationInfo: IPublicationInfo;
  citationSource: CitationSourceEnum;
}

/*
export enum OrdinalSuffixs {
  ST = 'st',
  ND = 'nd',
  RD = 'rd',
  TH = 'th',
}

export const removeFullstopAddComma = (str: string) => `${str.trim().slice(0, -1)},`;

export const removeLastCharacter = (str: string) => `${str.trim().slice(0, -1)}`;

export const cardinalToOrdinal = (number: number | string) => {
  number = Number.parseInt(number as string, 10);
  if (!Number.isInteger(number)) {
    return number;
  }
  const modulo = number % 10;
  let suffix = '';
  switch (modulo) {
    case 1:
      suffix = OrdinalSuffixs.ST;
      break;
    case 2:
      suffix = OrdinalSuffixs.ND;
      break;
    case 3:
      suffix = OrdinalSuffixs.RD;
      break;
    default:
      suffix = OrdinalSuffixs.TH;
      break;
  }
  const ordinal = number + suffix;
  return ordinal;
};

export const capitalizeAllFirstLetters = (text: string) => {
  const skipWords = ['a', 'an', 'the', 'for'];
  return text
    ?.split(' ')
    .map((word) => {
      if (!skipWords.includes(word)) return word.charAt(0).toUpperCase() + word.substring(1);
      return word;
    })
    .join(' ');
};

export const getContributorsNameInitials = (contributor: ContributorDetails) => {
  const firstName = contributor.firstName
    ? `${contributor.firstName?.charAt(0).toUpperCase()}.`
    : '';
  const middleName = contributor.middleName
    ? `${contributor.middleName?.charAt(0).toUpperCase()}.`
    : '';

  return { ...contributor, firstName, middleName };
};

export const formatFirstContributor = (
  contributor: ContributorDetails,
  citationStyle: CitationStylesEnum | string,
  numberOfContributors: number
) => {
  let contributorData = '';
  if (citationStyle === CitationStylesEnum.APA) {
    contributor = getContributorsNameInitials(contributor);
  }
  if (contributor.lastName) {
    contributorData += `${contributor.lastName}`;
  }
  if (contributor.firstName) {
    contributorData += contributor.lastName
      ? `, ${contributor.firstName}`
      : `${contributor.firstName}`;
  }
  if (contributor.middleName) {
    contributorData +=
      contributor.firstName || contributor.lastName
        ? ` ${contributor.middleName}`
        : `${contributor.middleName}`;
  }
  if (citationStyle === CitationStylesEnum.CHICAGO || citationStyle === CitationStylesEnum.MLA) {
    if (numberOfContributors === 1) {
      contributorData += '. ';
    } else {
      contributorData += ', ';
    }
  }
  if (citationStyle === CitationStylesEnum.APA) {
    contributorData += ' ';
  }
  return contributorData;
};

export const formatLastContributor = (
  contributor: ContributorDetails,
  numberOfContributors: number,
  citationStyle: CitationStylesEnum | string
) => {
  let contributorData = '';
  if (citationStyle === CitationStylesEnum.CHICAGO) {
    if (numberOfContributors > 1 && numberOfContributors < 11) contributorData += 'and ';
    if (contributor.firstName) {
      contributorData += `${contributor.firstName}`;
    }
    if (contributor.middleName) {
      contributorData += contributor.firstName
        ? ` ${contributor.middleName}`
        : `${contributor.middleName}`;
    }
    if (contributor.lastName) {
      contributorData +=
        contributor.middleName || contributor.firstName
          ? ` ${contributor.lastName}`
          : `${contributor.lastName}`;
    }
    contributorData += '. ';
  } else if (citationStyle === CitationStylesEnum.APA) {
    contributor = getContributorsNameInitials(contributor);
    if (numberOfContributors > 1) contributorData += '& ';
    if (contributor.lastName) {
      contributorData +=
        contributor.firstName || contributor.middleName
          ? `${contributor.lastName}, `
          : `${contributor.lastName}. `;
    }
    if (contributor.firstName) {
      contributorData += contributor.middleName
        ? `${contributor.firstName} `
        : `${contributor.firstName} `;
    }
    if (contributor.middleName) {
      contributorData += `${contributor.middleName} `;
    }
  } else if (citationStyle === CitationStylesEnum.MLA) {
    if (numberOfContributors > 1) contributorData += 'and ';
    if (contributor.firstName) {
      contributorData += `${contributor.firstName}`;
    }
    if (contributor.middleName) {
      contributorData += contributor.firstName
        ? ` ${contributor.middleName}`
        : `${contributor.middleName}`;
    }
    if (contributor.lastName) {
      contributorData +=
        contributor.middleName || contributor.firstName
          ? ` ${contributor.lastName}`
          : `${contributor.lastName}`;
    }
    contributorData += '. ';
  }
  return contributorData;
};

export const getNameInSequenceFML = (contributor: ContributorDetails) => {
  let name = '';
  if (contributor.firstName) {
    name += `${contributor.firstName}`;
  }
  if (contributor.middleName) {
    name += contributor.firstName ? ` ${contributor.middleName}` : `${contributor.middleName}`;
  }
  if (contributor.lastName) {
    name +=
      contributor.middleName || contributor.firstName
        ? ` ${contributor.lastName}`
        : `${contributor.lastName}`;
  }
  return name;
};

export const formatInbetweenContributors = (
  contributor: ContributorDetails,
  citationStyle: CitationStylesEnum | string
) => {
  let contributorData = '';
  if (citationStyle === CitationStylesEnum.APA) {
    contributor = getContributorsNameInitials(contributor);
  }

  if (contributor.firstName) {
    contributorData += `${contributor.firstName}`;
  }
  if (contributor.middleName) {
    contributorData += contributor.firstName
      ? ` ${contributor.middleName}`
      : `${contributor.middleName}`;
  }
  if (contributor.lastName) {
    contributorData +=
      contributor.middleName || contributor.firstName
        ? ` ${contributor.lastName}`
        : `${contributor.lastName}`;
  }
  contributorData += ', ';
  return contributorData;
};

export const createContributorData = ({
  contributors,
  citationStyle,
  contributorType,
  useSequentialNames,
}: {
  contributors: ContributorDetails[];
  citationStyle: CitationStylesEnum | string;
  contributorType: ContributorTypes;
  useSequentialNames?: boolean;
}) => {
  const numberOfContributors = contributors.length;
  contributors.forEach((contributor) => {
    contributor.firstName = capitalizeAllFirstLetters(contributor.firstName || '');
    contributor.middleName = capitalizeAllFirstLetters(contributor.middleName || '');
    contributor.lastName = capitalizeAllFirstLetters(contributor.lastName || '');
  });

  let contributorData = '';
  if (citationStyle === CitationStylesEnum.CHICAGO) {
    if (numberOfContributors <= 10) {
      contributors.forEach((contributor, contributorIndex) => {
        if (contributorIndex === 0 && !useSequentialNames) {
          contributorData += formatFirstContributor(
            contributor,
            citationStyle,
            numberOfContributors
          );
        } else if (contributorIndex === numberOfContributors - 1) {
          contributorData += formatLastContributor(
            contributor,
            numberOfContributors,
            citationStyle
          );
        } else if (useSequentialNames && numberOfContributors === 2) {
          contributorData += `${removeLastCharacter(
            formatInbetweenContributors(contributor, citationStyle)
          )} `;
        } else {
          contributorData += formatInbetweenContributors(contributor, citationStyle);
        }
      });
    } else {
      for (let contributorIndex = 0; contributorIndex < 7; contributorIndex += 1) {
        const contributor = contributors[contributorIndex];
        if (contributorIndex === 6) {
          contributorData += `${removeFullstopAddComma(
            formatLastContributor(contributor, numberOfContributors, citationStyle)
          )} et al. `;
        } else if (contributorIndex === 0 && !useSequentialNames) {
          contributorData += formatFirstContributor(
            contributor,
            citationStyle,
            numberOfContributors
          );
        } else {
          contributorData += formatInbetweenContributors(contributor, citationStyle);
        }
      }
    }
  } else if (citationStyle === CitationStylesEnum.APA) {
    // Author+Compiler Always Last, F. M.
    // Translator, Editor Always F. M. Last if they are not at front against Author+compiler
    // Otherwise for Translators, Editors foloow Last, F. M.
    if (numberOfContributors <= 20) {
      contributors.forEach((contributor, contributorIndex) => {
        if (!useSequentialNames) {
          if (contributorIndex === numberOfContributors - 1) {
            if (numberOfContributors === 1) {
              contributorData += `${formatFirstContributor(
                contributor,
                citationStyle,
                numberOfContributors
              )}`;
            } else {
              contributorData = `${contributorData.trim()}, ${formatLastContributor(
                contributor,
                numberOfContributors,
                citationStyle
              )}`;
            }
          } else if (contributorIndex !== numberOfContributors - 1) {
            if (contributorIndex === 0) {
              contributorData += `${formatFirstContributor(
                contributor,
                citationStyle,
                numberOfContributors
              ).trim()}`;
              contributorData += numberOfContributors > 1 ? ' ' : ', ';
            } else {
              contributorData = `${contributorData.trim()}, ${formatFirstContributor(
                contributor,
                citationStyle,
                numberOfContributors
              )}`;
            }
          }
        } else if (useSequentialNames) {
          if (contributorIndex === numberOfContributors - 1) {
            if (numberOfContributors === 1) {
              contributorData += `${removeLastCharacter(
                formatInbetweenContributors(contributor, citationStyle)
              )} `;
            } else {
              contributorData += `& ${removeLastCharacter(
                formatInbetweenContributors(contributor, citationStyle)
              )}`;
            }
          } else {
            contributorData += formatInbetweenContributors(contributor, citationStyle);
          }
        }
      });
    } else {
      for (let contributorIndex = 0; contributorIndex < 19; contributorIndex += 1) {
        const contributor = contributors[contributorIndex];
        if (!useSequentialNames) {
          contributorData += `${formatFirstContributor(
            contributor,
            citationStyle,
            numberOfContributors
          ).trim()}, `;
        } else if (useSequentialNames) {
          contributorData += formatInbetweenContributors(contributor, citationStyle);
        }
      }
      if (!useSequentialNames) {
        contributorData = `${contributorData.trim()}... ${formatFirstContributor(
          contributors[numberOfContributors - 1],
          citationStyle,
          numberOfContributors
        )}`;
      } else if (useSequentialNames) {
        contributorData = `${contributorData.trim()}... ${removeLastCharacter(
          formatInbetweenContributors(contributors[numberOfContributors - 1], citationStyle)
        )}`;
      }
    }
  } else if (citationStyle === CitationStylesEnum.MLA) {
    if (
      contributorType === ContributorTypes.EDITORS ||
      contributorType === ContributorTypes.TRANSLATORS
    ) {
      if (numberOfContributors <= 2) {
        contributors.forEach((contributor, contributorIndex) => {
          if (contributorIndex === 0 && !useSequentialNames) {
            contributorData += formatFirstContributor(
              contributor,
              citationStyle,
              numberOfContributors
            );
          } else if (contributorIndex === numberOfContributors - 1) {
            contributorData += formatLastContributor(
              contributor,
              numberOfContributors,
              citationStyle
            );
          } else if (useSequentialNames && numberOfContributors === 2) {
            contributorData += `${removeLastCharacter(
              formatInbetweenContributors(contributor, citationStyle)
            )} `;
          } else {
            contributorData += formatInbetweenContributors(contributor, citationStyle);
          }
        });
      } else {
        for (let contributorIndex = 0; contributorIndex < 1; contributorIndex += 1) {
          const contributor = contributors[contributorIndex];
          if (contributorIndex === 0 && !useSequentialNames) {
            contributorData += `${removeFullstopAddComma(
              formatFirstContributor(contributor, citationStyle, numberOfContributors)
            )} et al. `;
          } else if (contributorIndex === 0) {
            contributorData += `${getNameInSequenceFML(contributors[0])} et al. `;
          }
        }
      }
    } else if (
      contributorType === ContributorTypes.AUTHORS ||
      contributorType === ContributorTypes.COMPILERS
    ) {
      if (numberOfContributors <= 2) {
        contributors.forEach((contributor, contributorIndex) => {
          if (contributorIndex === 0) {
            contributorData += formatFirstContributor(
              contributor,
              citationStyle,
              numberOfContributors
            );
          } else if (contributorIndex === numberOfContributors - 1) {
            contributorData += formatLastContributor(
              contributor,
              numberOfContributors,
              citationStyle
            );
          } else {
            contributorData += formatInbetweenContributors(contributor, citationStyle);
          }
        });
      } else {
        contributorData += `${formatFirstContributor(
          contributors[0],
          citationStyle,
          numberOfContributors
        )}et al. `;
      }
    }
  }
  return contributorData;
};

export const createAuthorAndCompilerData = ({
  authors,
  compilers,
  citationStyle,
}: {
  [ContributorTypes.AUTHORS]: ContributorDetails[];
  [ContributorTypes.COMPILERS]: ContributorDetails[];
  citationStyle: CitationStylesEnum | string;
}) => {
  const authorsAndCompilersMerged = [...authors, ...compilers];
  return createContributorData({
    contributors: authorsAndCompilersMerged,
    citationStyle,
    contributorType: ContributorTypes.AUTHORS,
  });
};

export const createEditorsData = ({
  citationStyle,
  citationObject,
}: {
  citationObject: ICitationData;
  citationStyle: CitationStylesEnum | string;
}) => {
  const { contributors, publicationInfo } = citationObject;
  const { websiteTitle } = publicationInfo;
  const editors: ContributorDetails[] = contributors[ContributorTypes.EDITORS];
  const translators: ContributorDetails[] = contributors[ContributorTypes.TRANSLATORS];
  const authorsAndCompilersData = [
    ...contributors[ContributorTypes.AUTHORS],
    ...contributors[ContributorTypes.COMPILERS],
  ];
  if (citationStyle === CitationStylesEnum.CHICAGO) {
    if (authorsAndCompilersData.length) {
      return editors.length
        ? `Edited by ${createContributorData({
            contributors: editors,
            citationStyle,
            contributorType: ContributorTypes.EDITORS,
            useSequentialNames: true,
          })}`
        : '';
    }
    const editorsDataSuffix = editors.length > 1 ? 'eds. ' : 'ed. ';
    const editorsDataWithEtAl =
      editors.length > 10
        ? `${createContributorData({
            contributors: editors,
            citationStyle,
            contributorType: ContributorTypes.EDITORS,
          }).trim()},`
        : removeFullstopAddComma(
            createContributorData({
              contributors: editors,
              citationStyle,
              contributorType: ContributorTypes.EDITORS,
            })
          );
    return editors.length ? `${editorsDataWithEtAl} ${editorsDataSuffix}` : '';
  }
  if (citationStyle === CitationStylesEnum.APA) {
    let editorsDataSuffix = editors.length > 1 ? 'Eds. ' : 'Ed. ';
    if (authorsAndCompilersData.length > 0) {
      const editorsDataString = `${createContributorData({
        contributorType: ContributorTypes.EDITORS,
        contributors: editors,
        citationStyle,
        useSequentialNames: true,
      })}`;
      return editors.length ? `(${editorsDataString.trim()}, ${editorsDataSuffix.trim()}). ` : '';
    }
    const editorsDataString = `${createContributorData({
      contributorType: ContributorTypes.EDITORS,
      contributors: editors,
      citationStyle,
    })}`;
    editorsDataSuffix = `(${editorsDataSuffix.trim()}).`;
    return editors.length ? `${editorsDataString}${editorsDataSuffix} ` : '';
  }
  if (citationStyle === CitationStylesEnum.MLA) {
    if (authorsAndCompilersData.length) {
      let editorsDataString = `Edited by ${createContributorData({
        contributors: editors,
        citationStyle,
        contributorType: ContributorTypes.EDITORS,
        useSequentialNames: true,
      })}`;
      if (editors.length > 2) {
        if (websiteTitle) {
          editorsDataString = `${editorsDataString.trim()}, `;
        }
      }

      return editors.length ? `${editorsDataString}` : '';
    }
    const editorsDataSuffix = editors.length > 1 ? 'editors. ' : 'editor. ';
    const editorsDataWithEtAl =
      editors.length > 2
        ? `${createContributorData({
            contributors: editors,
            citationStyle,
            contributorType: ContributorTypes.EDITORS,
          }).trim()},`
        : removeFullstopAddComma(
            createContributorData({
              contributors: editors,
              citationStyle,
              contributorType: ContributorTypes.EDITORS,
            })
          );
    return editors.length ? `${editorsDataWithEtAl} ${editorsDataSuffix}` : '';
  }
};

export const createTranslatorsData = ({
  citationStyle,
  citationObject,
}: {
  citationStyle: CitationStylesEnum | string;
  citationObject: ICitationData;
}) => {
  const { contributors, publicationInfo, citationSource } = citationObject;
  const { websiteTitle, doi, volume, series, issue, citedPages, publishDate } = publicationInfo;
  const { start, end } = citedPages;
  const { year } = publishDate;
  const translators = contributors[ContributorTypes.TRANSLATORS];
  const editors = contributors[ContributorTypes.EDITORS];
  const authorsAndCompilersData = [
    ...contributors[ContributorTypes.AUTHORS],
    ...contributors[ContributorTypes.COMPILERS],
  ];
  if (citationStyle === CitationStylesEnum.CHICAGO) {
    if (authorsAndCompilersData.length > 0 || editors.length > 0) {
      return translators.length
        ? `Translated by ${createContributorData({
            contributors: translators,
            citationStyle,
            contributorType: ContributorTypes.TRANSLATORS,
            useSequentialNames: true,
          })}`
        : '';
    }
    const translatorsDataSuffix = translators.length > 1 ? 'trans. ' : 'trans. ';
    const translatorsDataWithEtAl =
      translators.length > 10
        ? `${createContributorData({
            contributors: translators,
            citationStyle,
            contributorType: ContributorTypes.TRANSLATORS,
          }).trim()},`
        : removeFullstopAddComma(
            createContributorData({
              contributors: translators,
              citationStyle,
              contributorType: ContributorTypes.TRANSLATORS,
            })
          );
    return translators.length ? `${translatorsDataWithEtAl} ${translatorsDataSuffix}` : '';
  }
  if (citationStyle === CitationStylesEnum.APA) {
    let translatorsDataSuffix = translators.length > 1 ? 'Trans. ' : 'Trans. ';
    if (authorsAndCompilersData.length > 0 || editors.length > 0) {
      const translatorsDataString = `${createContributorData({
        contributorType: ContributorTypes.TRANSLATORS,
        contributors: translators,
        citationStyle,
        useSequentialNames: true,
      })}`;
      return translators.length
        ? `(${translatorsDataString.trim()}, ${translatorsDataSuffix.trim()}). `
        : '';
    }
    const translatorsDataString = `${createContributorData({
      contributorType: ContributorTypes.TRANSLATORS,
      contributors: translators,
      citationStyle,
    })}`;
    translatorsDataSuffix = `(${translatorsDataSuffix.trim()}).`;
    return translators.length ? `${translatorsDataString}${translatorsDataSuffix} ` : '';
  }
  if (citationStyle === CitationStylesEnum.MLA) {
    if (authorsAndCompilersData.length > 0 || editors.length > 0) {
      let translatorsDataString = `Translated by ${createContributorData({
        contributors: translators,
        citationStyle,
        contributorType: ContributorTypes.TRANSLATORS,
        useSequentialNames: true,
      })}`;
      if (translators.length > 2) {
        translatorsDataString = translatorsDataString.trim();
        if (citationSource === CitationSourceEnum.JOURNAL) {
          if (volume || series || issue || start || end || year || doi) {
            translatorsDataString += ', ';
          }
        }
        if (citationSource === CitationSourceEnum.WEBSITE) {
          if (websiteTitle) {
            translatorsDataString += ', ';
          }
        }
      } else if (translators.length && translators.length <= 2) {
        if (citationSource === CitationSourceEnum.JOURNAL) {
          if (volume || series || issue || start || end || year || doi) {
            translatorsDataString = `${removeFullstopAddComma(translatorsDataString)} `;
          }
        }
      }
      // if (translators.length > 2) {
      //   translatorsDataString = `${translatorsDataString.trim()}! `;
      // }

      return translators.length ? translatorsDataString : '';
    }
    const translatorsDataSuffix = translators.length > 1 ? 'translators. ' : 'translator. ';
    let translatorsDataString = createContributorData({
      contributors: translators,
      citationStyle,
      contributorType: ContributorTypes.TRANSLATORS,
    });
    if (translators.length > 2) {
      translatorsDataString = `${translatorsDataString.trim()},`;
    } else {
      translatorsDataString = removeFullstopAddComma(translatorsDataString);
    }
    return translators.length ? `${translatorsDataString} ${translatorsDataSuffix}` : '';
  }
};

const formatTitle = ({
  citationObject,
  citationStyle,
}: {
  citationObject: ICitationData;
  citationStyle: CitationStylesEnum | string;
}) => {
  const { publicationInfo, contributors, citationSource } = citationObject;
  const { editors, authors, translators, compilers } = contributors;
  const authorsAndCompilersMerged = [...authors, ...compilers];
  const title = publicationInfo.title || '';
  if (citationStyle === CitationStylesEnum.CHICAGO) {
    if (citationSource === CitationSourceEnum.BOOK) {
      return `${capitalizeAllFirstLetters(title)}`;
    }
    if (citationSource === CitationSourceEnum.WEBSITE) {
      return `“${capitalizeAllFirstLetters(title)}.” `;
    }
    if (citationSource === CitationSourceEnum.JOURNAL) {
      if (
        publicationInfo.journalTitle ||
        (authorsAndCompilersMerged.length && (editors.length || translators.length)) ||
        (authorsAndCompilersMerged.length === 0 && editors.length && translators.length)
      ) {
        return `“${capitalizeAllFirstLetters(title)}.” `;
      }
      return `“${capitalizeAllFirstLetters(title)},” `;
    }
  } else if (citationStyle === CitationStylesEnum.APA) {
    if (citationSource === CitationSourceEnum.BOOK) {
      return `${capitalize(title)}. `;
    }
    if (citationSource === CitationSourceEnum.WEBSITE) {
      return `${capitalizeAllFirstLetters(title)}. `;
    }
    if (citationSource === CitationSourceEnum.JOURNAL) {
      return `${capitalizeAllFirstLetters(title)}. `;
    }
  } else if (citationStyle === CitationStylesEnum.MLA) {
    if (citationSource === CitationSourceEnum.BOOK) {
      return `${capitalizeAllFirstLetters(title)}`;
    }
    if (citationSource === CitationSourceEnum.WEBSITE) {
      return `“${capitalizeAllFirstLetters(title)}.” `;
    }
    if (citationSource === CitationSourceEnum.JOURNAL) {
      return `“${capitalizeAllFirstLetters(title)}.” `;
    }
  }
};

const formatPublicationDate = ({
  publicationInfo,
  citationSource,
  citationStyle,
}: {
  publicationInfo: IPublicationInfo;
  citationSource: CitationSourceEnum;
  citationStyle: CitationStylesEnum | string;
}) => {
  let formattedDate = '';
  const { publishDate: date } = publicationInfo;
  if (citationStyle === CitationStylesEnum.APA) {
    if (citationSource === CitationSourceEnum.BOOK) {
      if (date.year) {
        formattedDate += `(${date.year}). `;
      } else {
        formattedDate += '(n.d.). ';
      }
    } else if (citationSource === CitationSourceEnum.WEBSITE) {
      if (date.year) {
        formattedDate += date.month ? `(${date.year}, ` : `(${date.year}). `;
        if (date.month) {
          formattedDate += date.day ? `${date.month} ${date.day}). ` : `${date.month}). `;
        }
      } else {
        formattedDate += '(n.d.). ';
      }
    } else if (citationSource === CitationSourceEnum.JOURNAL) {
      if (date.year) {
        formattedDate += `(${date.year}). `;
      } else {
        formattedDate += '(n.d.). ';
      }
    }
  }
  return formattedDate;
};

const formatJournalTitle = ({
  citationObject,
  citationStyle,
}: {
  citationObject: ICitationData;
  citationStyle: string;
}) => {
  const { citationSource, publicationInfo, contributors } = citationObject;
  const authorsAndCompilersData = [
    ...contributors[ContributorTypes.AUTHORS],
    ...contributors[ContributorTypes.COMPILERS],
  ];
  const { editors, translators } = contributors;
  const { journalTitle, volume, series, issue } = publicationInfo;
  if (citationStyle === CitationStylesEnum.CHICAGO) {
    return `${journalTitle}, `;
  }
  if (citationStyle === CitationStylesEnum.APA) {
    if (authorsAndCompilersData.length || editors?.length || translators?.length) {
      return `. ${capitalizeAllFirstLetters(journalTitle as string)}`;
    }
    return `${capitalizeAllFirstLetters(journalTitle as string)}`;
  }
  if (citationStyle === CitationStylesEnum.MLA) {
    if (citationSource === CitationSourceEnum.JOURNAL) {
      // if (volume || series || issue) {
      if (authorsAndCompilersData.length && (editors.length || translators.length)) {
        return `${capitalizeAllFirstLetters(journalTitle as string)}. `;
      }
      if (authorsAndCompilersData.length === 0 && editors.length && translators.length) {
        return `${capitalizeAllFirstLetters(journalTitle as string)}. `;
      }
      return `${capitalizeAllFirstLetters(journalTitle as string)}, `;
    }
    return `${capitalizeAllFirstLetters(journalTitle as string)}. `;
  }
};

const formatWebsiteTitle = ({
  title,
  citationSource,
  citationStyle,
}: {
  title: string;
  citationSource: string;
  citationStyle: string;
}) => {
  if (
    title.length &&
    citationStyle === CitationStylesEnum.MLA &&
    citationSource === CitationSourceEnum.WEBSITE
  ) {
    return `${capitalizeAllFirstLetters(title)}, `;
  }
  return '';
};

export const createOtherPublicationData = ({
  publicationInfo,
  contributors,
  citationSource,
  citationStyle,
}: {
  publicationInfo: IPublicationInfo;
  contributors: IContributors;
  citationSource: CitationSourceEnum;
  citationStyle: CitationStylesEnum | string;
}) => {
  let publicationInfoData = '';
  const authorsAndCompilersData = [
    ...contributors[ContributorTypes.AUTHORS],
    ...contributors[ContributorTypes.COMPILERS],
  ];
  const translatorsData = contributors[ContributorTypes.TRANSLATORS];
  const editorsData = contributors[ContributorTypes.EDITORS];
  const { accessDate, publishDate, journalTitle } = publicationInfo;
  const isPublishDateAvailable = !!publishDate?.year;
  const isAccessDateAvailable = !!(accessDate?.year || accessDate?.month || accessDate?.day);
  if (citationStyle === CitationStylesEnum.CHICAGO) {
    if (citationSource === CitationSourceEnum.BOOK) {
      const { edition, totalVolumes, series, city, state, publisher } = publicationInfo;
      let { volume } = publicationInfo;
      if (volume === undefined || volume === null || (volume === '' && totalVolumes)) {
        volume = totalVolumes;
      }
      if (edition) {
        publicationInfoData += `${cardinalToOrdinal(edition)} ed. `;
      }
      if (volume) {
        publicationInfoData +=
          totalVolumes || series || city || publisher ? `Vol. ${volume}. ` : `Vol. ${volume}`;
      }
      if (totalVolumes) {
        publicationInfoData += `${totalVolumes} vols. `;
      }
      if (series) {
        publicationInfoData += city || publisher ? `${series}. ` : `${series}`;
      }
      if (city && state) {
        publicationInfoData += `${city}, ${state}: `;
      }
      if (city && !state) {
        publicationInfoData += `${city}: `;
      }
      if (publisher) {
        publicationInfoData += `${publisher}`;
      }
      if (
        publicationInfoData &&
        !(authorsAndCompilersData.length === 0 && editorsData?.length && translatorsData?.length)
      ) {
        publicationInfoData = `. ${publicationInfoData}`;
      }
      if (
        !Number.isNaN(Number.parseFloat(publishDate.year as string)) &&
        Number.parseInt(publishDate.year as string, 10) > 0
      ) {
        publicationInfoData += `, ${publishDate.year}.`;
      } else {
        publicationInfoData += ', n.d.';
      }
    }
    if (citationSource === CitationSourceEnum.WEBSITE) {
      const { websiteTitle, publisher, url } = publicationInfo;
      if (websiteTitle) {
        publicationInfoData += `${websiteTitle}. `;
      }
      if (publisher) {
        publicationInfoData += isPublishDateAvailable ? `${publisher}, ` : `${publisher}. `;
      }
      if (isPublishDateAvailable) {
        if (publishDate.month) {
          if (publishDate.day) {
            publicationInfoData += `${publishDate.month} ${publishDate.day},`;
          } else {
            publicationInfoData += `${publishDate.month} `;
          }
        }
        publicationInfoData = removeFullstopAddComma(publicationInfoData);
        publicationInfoData += ` ${publishDate?.year}. `;
      }
      if (!isPublishDateAvailable && isAccessDateAvailable) {
        publicationInfoData += 'Accessed ';
        if (accessDate.month) {
          if (accessDate.day) {
            publicationInfoData += `${accessDate.month} ${accessDate.day}, `;
          } else {
            publicationInfoData += `${accessDate.month} `;
          }
        }
        publicationInfoData += `${accessDate?.year}. `;
      }
      if (!isPublishDateAvailable && !isAccessDateAvailable) {
        publicationInfoData = `${removeFullstopAddComma(publicationInfoData)} n.d. `;
      }
      if (url) {
        publicationInfoData += `${url}.`;
      }
    }
    if (citationSource === CitationSourceEnum.JOURNAL) {
      const { issue, volume, series, citedPages, doi } = publicationInfo;
      if (series) {
        publicationInfoData += volume || issue ? `${series}, ` : `${series} `;
      }
      if (volume) {
        publicationInfoData += issue ? `${volume}, ` : `${volume} `;
      }
      if (issue) {
        publicationInfoData += `no. ${issue} `;
      }
      if (isPublishDateAvailable) {
        if (series || volume || issue) {
          publicationInfoData += '(';
          if (publishDate.month) {
            if (publishDate.day) {
              publicationInfoData += `${publishDate.month} ${publishDate.day}, `;
            } else {
              publicationInfoData += `${publishDate.month} `;
            }
          }
          publicationInfoData += publishDate.year;
          publicationInfoData += ')';
          publicationInfoData += citedPages.start || citedPages.end ? ': ' : '.';
        } else {
          if (publishDate.month) {
            if (publishDate.day) {
              publicationInfoData += `${publishDate.month} ${publishDate.day}, `;
            } else {
              publicationInfoData += `${publishDate.month} `;
            }
          }
          publicationInfoData += publishDate.year;
          publicationInfoData += citedPages.start || citedPages.end ? ', ' : '.';
        }
        if (doi && !(citedPages.start || citedPages.end)) {
          publicationInfoData += ' ';
        }
      }
      if (!isPublishDateAvailable) {
        if (series || volume || issue) {
          publicationInfoData += '(n.d.)';
          publicationInfoData += citedPages.start || citedPages.end ? ': ' : '.';
        } else {
          publicationInfoData += citedPages.start || citedPages.end ? 'n.d., ' : 'n.d.';
        }
        if (doi && !(citedPages.start || citedPages.end)) {
          publicationInfoData += ' ';
        }
      }
      if (citedPages.start || citedPages.end) {
        if (citedPages.start && citedPages.end) {
          publicationInfoData += `${citedPages.start}–${citedPages.end}. `;
        } else {
          publicationInfoData += citedPages.start ? `${citedPages.start}. ` : `${citedPages.end}. `;
        }
      }
      if (doi) {
        publicationInfoData += `${doi}.`;
      }
    }
  } else if (citationStyle === CitationStylesEnum.APA) {
    if (citationSource === CitationSourceEnum.BOOK) {
      const { totalVolumes, volume, series, edition, publisher } = publicationInfo;
      if (volume || series || edition) {
        publicationInfoData += '(';
        if (edition) {
          publicationInfoData += `${cardinalToOrdinal(edition)} ed.`;
        }
        if (volume) {
          publicationInfoData += edition
            ? `, Vol. ${volume || totalVolumes}`
            : `Vol. ${volume || totalVolumes}`;
        }
        if (series) {
          publicationInfoData += volume || edition ? `, Ser. ${series}` : `Ser. ${series}`;
        }
        publicationInfoData += '). ';
      }
      if (publisher) {
        publicationInfoData += `${capitalize(publisher)}.`;
      }
    } else if (citationSource === CitationSourceEnum.WEBSITE) {
      const { url, websiteTitle } = publicationInfo;
      if (websiteTitle) {
        publicationInfoData += url ? `${websiteTitle}. ${url}.` : `${websiteTitle}.`;
      }
      if (!websiteTitle && url) {
        publicationInfoData += `${url}.`;
      }
    } else if (citationSource === CitationSourceEnum.JOURNAL) {
      const { issue, volume, series, citedPages, doi } = publicationInfo;
      const { start, end } = citedPages;
      if (volume || issue || start || end) {
        publicationInfoData += ', ';
      } else if (doi) {
        publicationInfoData += '. ';
      }
      if (volume) {
        let suffixString = '. ';
        if (start || end) {
          suffixString = ', ';
        }
        publicationInfoData += issue
          ? `${volume}(${issue})${suffixString}`
          : `${volume}${suffixString}`;
      } else if (issue) {
        let suffixString = '. ';
        if (start || end) {
          suffixString = ', ';
        }
        publicationInfoData += `(${issue})${suffixString}`;
      }
      if (start || end) {
        if (start && end) {
          publicationInfoData += `${start}–${end}. `;
        } else {
          publicationInfoData += start ? `${start}. ` : `${end}. `;
        }
      }
      if (doi) {
        publicationInfoData += `${doi}.`;
      }
    }
  } else if (citationStyle === CitationStylesEnum.MLA) {
    if (citationSource === CitationSourceEnum.BOOK) {
      const { edition, totalVolumes, series, city, state, publisher } = publicationInfo;
      let { volume } = publicationInfo;
      if (volume === undefined || volume === null || (volume === '' && totalVolumes)) {
        volume = totalVolumes;
      }

      if (edition) {
        publicationInfoData +=
          volume || totalVolumes || series || publisher
            ? `${cardinalToOrdinal(edition)} ed., `
            : `${cardinalToOrdinal(edition)} ed.`;
      }
      if (volume) {
        publicationInfoData +=
          totalVolumes || series || publisher ? `vol. ${volume}, ` : `vol. ${volume} `;
      }
      if (totalVolumes) {
        publicationInfoData += series || publisher ? `${totalVolumes}, ` : `${totalVolumes} `;
      }
      if (series) {
        publicationInfoData += publisher ? `ser. ${series}, ` : `ser. ${series}`;
      }
      if (publisher) {
        publicationInfoData += `${capitalizeAllFirstLetters(publisher)}`;
      }
      let needCommaAfterEtAl = false;
      if (edition || volume || series || city || state || publisher) {
        if (authorsAndCompilersData.length > 0) {
          if (editorsData.length >= 0 && translatorsData.length > 2) {
            publicationInfoData = `, ${publicationInfoData}`;
            needCommaAfterEtAl = true;
          } else if (translatorsData.length === 0 && editorsData.length > 2) {
            publicationInfoData = `, ${publicationInfoData}`;
            needCommaAfterEtAl = true;
          }
        }
        if (authorsAndCompilersData.length === 0) {
          if (editorsData.length > 0 && translatorsData.length > 2) {
            publicationInfoData = `, ${publicationInfoData}`;
            needCommaAfterEtAl = true;
          }
        }
      }
      if (
        publicationInfoData &&
        !(authorsAndCompilersData.length === 0 && editorsData?.length && translatorsData?.length)
      ) {
        publicationInfoData = needCommaAfterEtAl
          ? `.${publicationInfoData}`
          : `. ${publicationInfoData}`;
      }
      if (
        !Number.isNaN(Number.parseFloat(publishDate.year as string)) &&
        Number.parseInt(publishDate.year as string, 10) > 0
      ) {
        // We can remove month, day option in MLA Book from UI (as per citation machine)
        // if (publishDate.month) {
        //   publicationInfoData += publishDate.day
        //     ? `${publishDate.day} ${publishDate.month}. `
        //     : `${publishDate.month}. `;
        // }
        publicationInfoData += `, ${publishDate.year}.`;
      }
    } else if (citationSource === CitationSourceEnum.WEBSITE) {
      const { publisher, url } = publicationInfo;
      if (publisher) {
        publicationInfoData +=
          isPublishDateAvailable || url
            ? `${capitalizeAllFirstLetters(publisher)}, `
            : `${capitalizeAllFirstLetters(publisher)}. `;
      }
      if (isPublishDateAvailable) {
        if (publishDate.month) {
          publicationInfoData += publishDate.day
            ? `${publishDate.day} ${publishDate.month}. `
            : `${publishDate.month}. `;
        }
        publicationInfoData += `${publishDate?.year}, `;
      }
      if (publishDate.time) {
        publicationInfoData += url ? `${publishDate.time}, ` : `${publishDate.time}.`;
      }
      if (url) {
        publicationInfoData += `${url}.`;
      }
    } else if (citationSource === CitationSourceEnum.JOURNAL) {
      let useFirstLetterAsCapital = false;
      if (!journalTitle) {
        if (!editorsData.length && !translatorsData.length) {
          useFirstLetterAsCapital = true;
        }
        if (
          authorsAndCompilersData.length === 0 &&
          (editorsData.length || translatorsData.length) &&
          !(editorsData.length && translatorsData.length)
        ) {
          useFirstLetterAsCapital = true;
        }
      }

      const { issue, volume, series, citedPages, doi } = publicationInfo;
      if (volume) {
        const prefixString = useFirstLetterAsCapital ? 'Vol.' : 'vol.';
        if (useFirstLetterAsCapital) useFirstLetterAsCapital = false;
        publicationInfoData +=
          issue || series || isPublishDateAvailable || citedPages.start || citedPages.end || doi
            ? `${prefixString} ${volume}, `
            : `${prefixString} ${volume}`;
      }
      if (issue) {
        const prefixString = useFirstLetterAsCapital ? 'No.' : 'no.';
        if (useFirstLetterAsCapital) useFirstLetterAsCapital = false;
        publicationInfoData +=
          series || isPublishDateAvailable || citedPages.start || citedPages.end || doi
            ? `${prefixString} ${issue}, `
            : `${prefixString} ${issue}`;
      }
      if (series) {
        const prefixString = useFirstLetterAsCapital ? 'Ser.' : 'ser.';
        if (useFirstLetterAsCapital) useFirstLetterAsCapital = false;
        publicationInfoData +=
          isPublishDateAvailable || citedPages.start || citedPages.end || doi
            ? `${prefixString} ${series}, `
            : `${prefixString} ${series}`;
      }
      if (isPublishDateAvailable) {
        if (publishDate.month) {
          publicationInfoData += publishDate.day
            ? `${publishDate.day} ${publishDate.month}. `
            : `${publishDate.month}. `;
        }

        publicationInfoData += `${publishDate?.year}`;
        if (citedPages.start || citedPages.end || doi) {
          publicationInfoData += ', ';
        } else {
          publicationInfoData += '.';
        }
      }
      if (citedPages.start || citedPages.end) {
        if (citedPages.start && citedPages.end) {
          publicationInfoData += `pp. ${citedPages.start}–${citedPages.end}. `;
        } else {
          publicationInfoData += citedPages.start
            ? `pp. ${citedPages.start}. `
            : `pp. ${citedPages.end}. `;
        }
        if (doi) {
          publicationInfoData = `${publicationInfoData.trim()}, `;
        }
      }
      // series missing again
      if (doi) {
        publicationInfoData += `doi:${doi}.`;
      }
    }
  }
  return publicationInfoData;
};
*/

/* export const createPublicationInfoData = (
  publicationInfo: IPublicationInfo,
  citationSource: CitationSourceEnum,
  citationStyle: CitationStylesEnum
) => ({
  title: publicationInfo.title?.length
    ? formatTitle({ title: publicationInfo.title, citationSource, citationStyle })
    : '',
  otherData: createOtherPublicationData({ publicationInfo, citationSource, citationStyle }),
});
 */
/* const pushCitationChunkToCreateCompleteCitation = ({
  citationStyle,
  citationSource,
}: {
  citationStyle: CitationStylesEnum;
  citationSource: CitationSourceEnum;
}) => {
  const citationChunkArray = []
  if(citationStyle === CitationStylesEnum.CHICAGO) {
    if(citationSource === CitationSourceEnum.BOOK){
      citationChunkArray.push()
    }
  }
}; */

const documentStyles = {
  documentHeading: 'Bibliography',
  fontFamily: 'Times New Roman',
  fontSize: 24,
  spacing: {
    belowHeading: 1,
    betweenCitations: 0,
    betweenLines: 240,
  },
};

/*
const trimAndRemoveLastCharacter = (test: string) => test.trim().slice(0, -1);

const combineEditorsAndTranslatorsData = (
  citationStyle: CitationStylesEnum | string,
  citationObject: ICitationData
) => {
  const { contributors, publicationInfo, citationSource } = citationObject;
  const { edition, volume, series, city, state, publisher } = publicationInfo;
  const authorsAndCompilersData = [
    ...contributors[ContributorTypes.AUTHORS],
    ...contributors[ContributorTypes.COMPILERS],
  ];
  let titleData = publicationInfo.title?.length
    ? `${formatTitle({ citationObject, citationStyle })}`
    : '';

  const publicationDate =
    citationStyle === CitationStylesEnum.APA && authorsAndCompilersData.length === 0
      ? `${formatPublicationDate({
          publicationInfo,
          citationStyle,
          citationSource,
        })}`
      : '';

  let translatorsData =
    contributors[ContributorTypes.TRANSLATORS].length > 0
      ? createTranslatorsData({
          citationStyle,
          citationObject,
        })
      : '';

  let editorsData =
    contributors[ContributorTypes.EDITORS].length > 0
      ? createEditorsData({
          citationObject,
          citationStyle,
        })
      : '';
  const journalData = publicationInfo.journalTitle?.length
    ? formatJournalTitle({
        citationObject,
        citationStyle,
      })
    : '';

  if (citationStyle === CitationStylesEnum.MLA && citationSource === CitationSourceEnum.JOURNAL) {
    let showEditorsDataAtFirst = false;
    let showTranslatorsDataAtFirst = false;
    if (authorsAndCompilersData.length === 0) {
      if (contributors.editors.length) {
        showEditorsDataAtFirst = true;
      } else if (contributors.translators.length) {
        showTranslatorsDataAtFirst = true;
      }
    }
    return {
      exportData: [
        new CitationTextRun({
          text: showEditorsDataAtFirst ? editorsData : '',
          size: 24,
          font: documentStyles.fontFamily,
        }),
        new CitationTextRun({
          text: showTranslatorsDataAtFirst ? translatorsData : '',
          size: 24,
          font: documentStyles.fontFamily,
        }),
        new CitationTextRun({
          text: titleData,
          size: 24,
          font: documentStyles.fontFamily,
        }),
        new CitationTextRun({
          text: journalData,
          size: 24,
          italics: citationSource === CitationSourceEnum.JOURNAL,
          font: documentStyles.fontFamily,
        }),
        new CitationTextRun({
          text: !showEditorsDataAtFirst ? editorsData : '',
          size: 24,
          font: documentStyles.fontFamily,
        }),
        new CitationTextRun({
          text: !showTranslatorsDataAtFirst ? translatorsData : '',
          size: 24,
          font: documentStyles.fontFamily,
        }),
      ],
      previewData: [
        { text: showEditorsDataAtFirst ? editorsData : '', style: '' },
        { text: showTranslatorsDataAtFirst ? translatorsData : '', style: '' },
        { text: titleData, style: '' },
        { text: journalData, style: citationSource === CitationSourceEnum.JOURNAL ? 'italic' : '' },
        { text: !showEditorsDataAtFirst ? editorsData : '', style: '' },
        { text: !showTranslatorsDataAtFirst ? translatorsData : '', style: '' },
      ],
    };
  }

  if (
    (authorsAndCompilersData.length === 0 &&
      contributors.editors.length &&
      contributors.translators.length) ||
    (authorsAndCompilersData.length === 0 &&
      contributors.editors.length === 0 &&
      contributors.translators.length === 0)
  ) {
    // titleData = titleData;
  } else {
    titleData =
      citationSource === CitationSourceEnum.JOURNAL &&
      (citationStyle === CitationStylesEnum.APA || citationStyle === CitationStylesEnum.CHICAGO)
        ? titleData
        : '';
  }
  if (citationStyle === CitationStylesEnum.APA) {
    if (citationSource === CitationSourceEnum.BOOK) {
      if ((edition || volume || series) && !(translatorsData?.length || editorsData?.length)) {
        titleData = `${removeLastCharacter(titleData)}`;
      }
    }
    if (citationSource === CitationSourceEnum.JOURNAL) {
      // publicationDate = `${removeLastCharacter(publicationDate)}`;
      if (authorsAndCompilersData.length || editorsData?.length || translatorsData?.length) {
        titleData = `${removeLastCharacter(titleData as string)}`;
      }
    }

    if (
      citationSource === CitationSourceEnum.BOOK ||
      citationSource === CitationSourceEnum.WEBSITE
    ) {
      let editorAndTranslatorString = '';
      if (authorsAndCompilersData.length > 0 && translatorsData && editorsData) {
        editorAndTranslatorString = `${translatorsData.trim().slice(0, -2)}, ${editorsData.slice(
          1
        )}`;
        translatorsData = editorAndTranslatorString;
        editorsData = '';
      }

      if (citationSource === CitationSourceEnum.WEBSITE) {
        if (authorsAndCompilersData.length) {
          translatorsData = '';
          editorsData = '';
        } else if (translatorsData && editorsData) {
          translatorsData = '';
        }
      }

      return {
        exportData: [
          new CitationTextRun({
            text: editorsData,
            size: 24,
            font: documentStyles.fontFamily,
          }),
          new CitationTextRun({
            text:
              authorsAndCompilersData.length === 0 && editorsData?.length === 0
                ? translatorsData
                : '',
            size: 24,
            font: documentStyles.fontFamily,
          }),
          new CitationTextRun({
            text:
              authorsAndCompilersData.length || editorsData?.length || translatorsData?.length
                ? `${publicationDate}`
                : '',
            size: 24,
            font: documentStyles.fontFamily,
          }),
          new CitationTextRun({
            text: titleData,
            size: 24,
            font: documentStyles.fontFamily,
            italics: true,
          }),
          new CitationTextRun({
            text:
              authorsAndCompilersData.length === 0 &&
              editorsData?.length === 0 &&
              translatorsData?.length === 0
                ? `${publicationDate}`
                : '',
            size: 24,
            font: documentStyles.fontFamily,
          }),
          new CitationTextRun({
            text: authorsAndCompilersData.length || editorsData?.length ? translatorsData : '',
            size: 24,
            font: documentStyles.fontFamily,
          }),
          new CitationTextRun({
            text: journalData,
            size: 24,
            font: documentStyles.fontFamily,
          }),
        ],
        previewData: [
          {
            text: `${editorsData}`,
            style: '',
          },
          {
            text:
              authorsAndCompilersData.length === 0 && editorsData?.length === 0
                ? translatorsData
                : '',
            style: '',
          },
          {
            text:
              authorsAndCompilersData.length || editorsData?.length || translatorsData?.length
                ? `${publicationDate}`
                : '',
            style: '',
          },
          {
            text: `${titleData}`,
            style: 'italics',
          },
          {
            text:
              authorsAndCompilersData.length === 0 &&
              editorsData?.length === 0 &&
              translatorsData?.length === 0
                ? `${publicationDate}`
                : '',
            style: '',
          },
          {
            text: authorsAndCompilersData.length || editorsData?.length ? translatorsData : '',
            style: '',
          },
          {
            text: `${journalData}`,
            style: '',
          },
        ],
      };
    }
    if (citationSource === CitationSourceEnum.JOURNAL) {
      let showEditorsData = false;
      let showTranslatorsData = false;
      if (authorsAndCompilersData.length === 0) {
        if (contributors.editors.length) {
          showEditorsData = true;
        } else if (contributors.translators.length) {
          showTranslatorsData = true;
        }
      }
      return {
        exportData: [
          new CitationTextRun({
            text: showEditorsData ? editorsData : '',
            size: 24,
            font: documentStyles.fontFamily,
          }),
          new CitationTextRun({
            text: showTranslatorsData ? translatorsData : '',
            size: 24,
            font: documentStyles.fontFamily,
          }),
          new CitationTextRun({
            text:
              authorsAndCompilersData.length || editorsData?.length || translatorsData?.length
                ? `${publicationDate}`
                : '',
            size: 24,
            font: documentStyles.fontFamily,
          }),
          new CitationTextRun({
            text: titleData,
            size: 24,
            font: documentStyles.fontFamily,
          }),
          new CitationTextRun({
            text:
              authorsAndCompilersData.length === 0 &&
              editorsData?.length === 0 &&
              translatorsData?.length === 0
                ? `${publicationDate}`
                : '',
            size: 24,
            font: documentStyles.fontFamily,
          }),
          new CitationTextRun({
            text: journalData,
            size: 24,
            italics: citationSource === CitationSourceEnum.JOURNAL,
            font: documentStyles.fontFamily,
          }),
          // new CitationTextRun({
          //   text: authorsAndCompilersData.length || editorsData?.length ? translatorsData : '',
          //   size: 24,
          //   font: documentStyles.fontFamily,
          // }),
        ],
        previewData: [
          {
            text: showEditorsData ? editorsData : '',
            style: '',
          },
          {
            text: showTranslatorsData ? translatorsData : '',
            style: '',
          },
          {
            text:
              authorsAndCompilersData.length || editorsData?.length || translatorsData?.length
                ? `${publicationDate}`
                : '',
            style: '',
          },
          { text: titleData, style: '' },
          {
            text:
              authorsAndCompilersData.length === 0 &&
              editorsData?.length === 0 &&
              translatorsData?.length === 0
                ? `${publicationDate}`
                : '',
            style: '',
          },
          {
            text: journalData,
            style: citationSource === CitationSourceEnum.JOURNAL ? 'italic' : '',
          },
          // {
          //   text: authorsAndCompilersData.length || editorsData?.length ? translatorsData : '',
          //   style: '',
          // },
        ],
      };
    }
  }

  if (authorsAndCompilersData.length) {
    if (translatorsData?.length && citationSource !== CitationSourceEnum.JOURNAL) {
      translatorsData = trimAndRemoveLastCharacter(translatorsData as string);
    }
    if (editorsData?.length && !translatorsData?.length) {
      if (
        !(citationSource === CitationSourceEnum.WEBSITE && citationStyle === CitationStylesEnum.MLA)
      ) {
        editorsData = trimAndRemoveLastCharacter(editorsData as string);
      }
    }
  }

  if (
    citationStyle === CitationStylesEnum.CHICAGO &&
    citationSource === CitationSourceEnum.JOURNAL
  ) {
    let showEditorsDataAtFirst = false;
    let showTranslatorsDataAtFirst = false;
    if (authorsAndCompilersData.length === 0) {
      if (contributors.editors.length) {
        showEditorsDataAtFirst = true;
      } else if (contributors.translators.length) {
        showTranslatorsDataAtFirst = true;
      }
    }
    return {
      exportData: [
        new CitationTextRun({
          text: showEditorsDataAtFirst ? editorsData : '',
          size: 24,
          font: documentStyles.fontFamily,
        }),
        new CitationTextRun({
          text: showTranslatorsDataAtFirst ? translatorsData : '',
          size: 24,
          font: documentStyles.fontFamily,
        }),
        new CitationTextRun({
          text: titleData,
          size: 24,
          font: documentStyles.fontFamily,
        }),
        new CitationTextRun({
          text: !showEditorsDataAtFirst ? editorsData : '',
          size: 24,
          font: documentStyles.fontFamily,
        }),
        new CitationTextRun({
          text: !showTranslatorsDataAtFirst ? translatorsData : '',
          size: 24,
          font: documentStyles.fontFamily,
        }),
        new CitationTextRun({
          text: journalData,
          size: 24,
          italics: citationSource === CitationSourceEnum.JOURNAL,
          font: documentStyles.fontFamily,
        }),
      ],
      previewData: [
        {
          text: showEditorsDataAtFirst ? editorsData : '',
          style: '',
        },
        { text: showTranslatorsDataAtFirst ? translatorsData : '', style: '' },
        {
          text: titleData,
          style: '',
        },
        {
          text: !showEditorsDataAtFirst ? editorsData : '',
          style: '',
        },
        {
          text: !showTranslatorsDataAtFirst ? translatorsData : '',
          style: '',
        },
        { text: journalData, style: citationSource === CitationSourceEnum.JOURNAL ? 'italic' : '' },
      ],
    };
  }

  let prefixText = '';
  if (authorsAndCompilersData.length) {
    if (editorsData?.length || translatorsData?.length) {
      if (citationStyle === CitationStylesEnum.CHICAGO) {
        if (citationSource !== CitationSourceEnum.JOURNAL) {
          prefixText = '. ';
        }
      } else if (citationStyle === CitationStylesEnum.MLA) {
        if (citationSource === CitationSourceEnum.BOOK) {
          prefixText = '. ';
        }
      }
    }
  }

  return {
    exportData: [
      new CitationTextRun({
        text: prefixText,
        size: 24,
        font: documentStyles.fontFamily,
      }),
      new CitationTextRun({
        text: editorsData,
        size: 24,
        font: documentStyles.fontFamily,
      }),
      new CitationTextRun({
        text: titleData,
        size: 24,
        font: documentStyles.fontFamily,
        italics: true,
      }),
      new CitationTextRun({
        text:
          authorsAndCompilersData.length === 0 &&
          editorsData?.length &&
          translatorsData?.length &&
          citationSource === CitationSourceEnum.BOOK
            ? '. '
            : '',
        size: 24,
        font: documentStyles.fontFamily,
      }),
      new CitationTextRun({
        text: translatorsData,
        size: 24,
        font: documentStyles.fontFamily,
      }),
      new CitationTextRun({
        text: journalData,
        size: 24,
        italics: citationSource === CitationSourceEnum.JOURNAL,
        font: documentStyles.fontFamily,
      }),
    ],
    previewData: [
      {
        text: prefixText,
        style: '',
      },
      { text: editorsData, style: '' },
      {
        text: titleData,
        style: 'italic',
      },
      {
        text:
          authorsAndCompilersData.length === 0 &&
          editorsData?.length &&
          translatorsData?.length &&
          citationSource === CitationSourceEnum.BOOK
            ? '. '
            : '',
        style: '',
      },
      {
        text: translatorsData,
        style: '',
      },
      { text: journalData, style: citationSource === CitationSourceEnum.JOURNAL ? 'italic' : '' },
    ],
  };
};

export const convertToExportableFormat = (allCitations: any[]) => {
  const allFormattedCitations = [] as ICitationData[];
  allCitations.forEach((citation) => {
    let formattedCitation = {
      contributors: {
        [ContributorTypes.AUTHORS]: [] as ContributorDetails[],
        [ContributorTypes.COMPILERS]: [] as ContributorDetails[],
        [ContributorTypes.EDITORS]: [] as ContributorDetails[],
        [ContributorTypes.TRANSLATORS]: [] as ContributorDetails[],
        [ContributorTypes.ILLUSTRATORS]: [] as ContributorDetails[],
      },
      publicationInfo: {} as IPublicationInfo,
      citationSource: {} as CitationSourceEnum,
    } as ICitationData;
    citation.contributors.forEach((contributor: Contributor) => {
      formattedCitation.contributors[contributor.contributorType]?.push({
        suffix: contributor.suffix,
        firstName: contributor.firstName,
        middleName: contributor.middleName,
        lastName: contributor.lastName,
      });
    });
    if (citation.citationSource) {
      formattedCitation.citationSource = citation.citationSource;
    }
    const defaultValuesObject = {
      publisher: '',
      title: '',
      subTitle: '',
      websiteTitle: '',
      journalTitle: '',
      url: '',
      issue: '',
      volume: '',
      edition: '',
      totalVolumes: '',
      series: '',
      state: '',
      city: '',
      doi: '',
      citedPages: {},
      publishDate: {},
      accessDate: {},
    };
    formattedCitation = {
      ...formattedCitation,
      publicationInfo: {
        ...defaultValuesObject,
        ...citation.publicationInfo,
      },
    };
    allFormattedCitations.push(formattedCitation);
  });

  return allFormattedCitations;
};

export const generatePreview = (
  allCitations: any[],
  citationStyle: CitationStylesEnum | string,
  skipConversion = false,
  returnExportText = false
) => {
  let allFormattedCitations: ICitationData[] = [];
  if (skipConversion) {
    allFormattedCitations = allCitations;
  } else {
    allFormattedCitations = convertToExportableFormat(allCitations);
  }

  return allFormattedCitations.map((citationObject: ICitationData) => {
    const { citationSource, contributors, publicationInfo } = citationObject;
    const { translators, editors, compilers, authors } = contributors;
    const { issue, volume, series, edition } = publicationInfo;
    const authorsCompilersData = `${createAuthorAndCompilerData({
      authors: contributors[ContributorTypes.AUTHORS],
      compilers: contributors[ContributorTypes.COMPILERS],
      citationStyle,
    })}`;
    let publicationDate = `${formatPublicationDate({
      publicationInfo,
      citationStyle,
      citationSource,
    })}`;
    let titleData = publicationInfo.title?.length
      ? formatTitle({ citationObject, citationStyle })
      : '';
    if (
      (authorsCompilersData.length === 0 &&
        contributors.editors.length &&
        contributors.translators.length) ||
      (authorsCompilersData.length === 0 &&
        contributors.editors.length === 0 &&
        contributors.translators.length === 0) ||
      (citationStyle === CitationStylesEnum.MLA && citationSource === CitationSourceEnum.JOURNAL)
    ) {
      titleData = '';
    } else {
      titleData =
        citationSource === CitationSourceEnum.JOURNAL &&
        (citationStyle === CitationStylesEnum.APA || citationStyle === CitationStylesEnum.CHICAGO)
          ? ''
          : titleData;
    }

    if (citationStyle === CitationStylesEnum.APA) {
      if (authorsCompilersData.length === 0) {
        publicationDate = '';
      }
      if (citationSource === CitationSourceEnum.BOOK) {
        if ((edition || volume || series) && !(translators.length || editors.length)) {
          titleData = `${removeLastCharacter(titleData as string)} `;
        }
      }
      if (citationSource === CitationSourceEnum.JOURNAL) {
        titleData = `${removeLastCharacter(titleData as string)}`;
      }
    }

    const previewObjectArray = [
      {
        text:
          authorsCompilersData.length > 0
            ? `${authorsCompilersData}${publicationDate}`
            : `${authorsCompilersData}`,
        style: '',
      },
      ...(authorsCompilersData.length > 0
        ? [
            {
              text: '',
              style: '',
            },
          ]
        : combineEditorsAndTranslatorsData(citationStyle, citationObject).previewData),
      {
        text: `${titleData}`,
        style:
          citationSource === CitationSourceEnum.BOOK ||
          (citationStyle === CitationStylesEnum.APA &&
            citationSource !== CitationSourceEnum.JOURNAL)
            ? 'italic'
            : '',
      },
      {
        text: authorsCompilersData.length > 0 ? '' : `${publicationDate}`,
        style: '',
      },
      ...(authorsCompilersData.length > 0
        ? combineEditorsAndTranslatorsData(citationStyle, citationObject).previewData
        : [
            {
              text: '',
              style: '',
            },
          ]),
      {
        text: formatWebsiteTitle({
          title: publicationInfo.websiteTitle || '',
          citationSource,
          citationStyle,
        }),
        style: 'italic',
      },
      {
        text: createOtherPublicationData({
          publicationInfo,
          contributors,
          citationSource,
          citationStyle,
        }),
        style: '',
      },
    ];
    for (let i = previewObjectArray.length - 1; i >= 0; i -= 1) {
      if (
        previewObjectArray[i].text?.trim() !== '' ||
        /s+$/.test(previewObjectArray[i].text || '')
      ) {
        previewObjectArray[i].text = previewObjectArray[i].text?.trimEnd();
        break;
      }
    }

    const previewString = previewObjectArray.map((previewObject) => previewObject.text).join('');
    if (previewString.trim().slice(-1) !== '.' && !returnExportText) {
      previewObjectArray.push({
        text: '.',
        style: '',
      });
    }

    return previewObjectArray;
  });
};

export const generateCitation = (
  citationObject: ICitationData,
  citationStyle: CitationStylesEnum | string
) => {
  const { contributors, publicationInfo, citationSource } = citationObject;
  const { translators, editors, compilers, authors } = contributors;
  const { issue, volume, series, edition } = publicationInfo;
  // if (citationStyle === CitationStylesEnum.APA) {
  //   documentStyles.documentHeading = 'References';
  //   documentStyles.spacing = {
  //     belowHeading: 0,
  //     betweenCitations: 0,
  //     betweenLines: 480,
  //   };
  // } else if (citationStyle === CitationStylesEnum.MLA) {
  //   documentStyles.documentHeading = 'Works Cited';
  //   documentStyles.spacing = {
  //     belowHeading: 0,
  //     betweenCitations: 0,
  //     betweenLines: 480,
  //   };
  // } else if (citationStyle === CitationStylesEnum.CHICAGO) {
  //   documentStyles.documentHeading = 'Bibliography';
  //   documentStyles.spacing = {
  //     belowHeading: 1,
  //     betweenCitations: 0,
  //     betweenLines: 240,
  //   };
  // }
  const authorsCompilersData = `${createAuthorAndCompilerData({
    authors: contributors[ContributorTypes.AUTHORS],
    compilers: contributors[ContributorTypes.COMPILERS],
    citationStyle,
  })}`;
  let publicationDate = `${formatPublicationDate({
    publicationInfo,
    citationStyle,
    citationSource,
  })}`;
  let titleData = publicationInfo.title?.length
    ? formatTitle({ citationObject, citationStyle })
    : '';
  if (
    (authorsCompilersData.length === 0 &&
      contributors.editors.length &&
      contributors.translators.length) ||
    (authorsCompilersData.length === 0 &&
      contributors.editors.length === 0 &&
      contributors.translators.length === 0) ||
    (citationStyle === CitationStylesEnum.MLA && citationSource === CitationSourceEnum.JOURNAL)
  ) {
    titleData = '';
  } else {
    titleData =
      citationSource === CitationSourceEnum.JOURNAL &&
      (citationStyle === CitationStylesEnum.APA || citationStyle === CitationStylesEnum.CHICAGO)
        ? ''
        : titleData;
  }

  if (citationStyle === CitationStylesEnum.APA) {
    if (authorsCompilersData.length === 0) {
      publicationDate = '';
    }
    if (citationSource === CitationSourceEnum.BOOK) {
      if ((edition || volume || series) && !(translators.length || editors.length)) {
        titleData = `${removeLastCharacter(titleData as string)} `;
      }
    }
    if (citationSource === CitationSourceEnum.JOURNAL) {
      titleData = `${removeLastCharacter(titleData as string)}`;
    }
  }

  const previewString = generatePreview(
    [citationObject],
    citationStyle as unknown as CitationStylesEnum,
    true,
    true
  )
    .map((citationDetails) => citationDetails.map((citationDetail) => citationDetail.text).join(''))
    .join('');

  const paras = new CitationParagraph({
    indent: { start: 1000, hanging: 1000 },
    spacing: {
      line: documentStyles.spacing.betweenLines,
    },
    children: [
      // Authors and Compilers
      new CitationTextRun({
        text:
          authorsCompilersData.length > 0
            ? `${authorsCompilersData}${publicationDate}`
            : `${authorsCompilersData}`,
        size: 24,
        font: documentStyles.fontFamily,
      }),
      ...(authorsCompilersData.length > 0
        ? [
            new CitationTextRun({
              text: '',
              size: 24,
              font: documentStyles.fontFamily,
            }),
          ]
        : combineEditorsAndTranslatorsData(citationStyle, citationObject).exportData),
      new CitationTextRun({
        text: `${titleData}`,
        italics:
          citationSource === CitationSourceEnum.BOOK ||
          (citationStyle === CitationStylesEnum.APA &&
            citationSource !== CitationSourceEnum.JOURNAL),
        size: 24,
        font: documentStyles.fontFamily,
      }),
      new CitationTextRun({
        text: authorsCompilersData.length > 0 ? '' : `${publicationDate}`,
        size: 24,
        font: documentStyles.fontFamily,
      }),
      ...(authorsCompilersData.length > 0
        ? combineEditorsAndTranslatorsData(citationStyle, citationObject).exportData
        : [
            new CitationTextRun({
              text: '',
              size: 24,
              font: documentStyles.fontFamily,
            }),
          ]),
      new CitationTextRun({
        text: formatWebsiteTitle({
          title: publicationInfo.websiteTitle || '',
          citationSource,
          citationStyle,
        }),
        size: 24,
        italics: true,
        font: documentStyles.fontFamily,
      }),
      new CitationTextRun({
        text: createOtherPublicationData({
          publicationInfo,
          contributors,
          citationSource,
          citationStyle,
        }),
        size: 24,
        font: documentStyles.fontFamily,
      }),
    ],
  });

  for (let i = paras.children.length - 1; i >= 0; i -= 1) {
    if (paras.children[i].text?.trim() !== '' || /s+$/.test(paras.children[i].text || '')) {
      paras.children[i].text = paras.children[i].text?.trimEnd();
      break;
    }
  }

  let citationStringSuffix = '';
  if (previewString.trim().slice(-1) !== '.') {
    citationStringSuffix = '.';
  }
  paras.children = [
    ...paras.children,
    new CitationTextRun({
      text: citationStringSuffix,
      size: 24,
      font: documentStyles.fontFamily,
    }),
    new CitationTextRun({
      text: '',
      size: 24,
      breakValue: documentStyles.spacing.betweenCitations,
      font: documentStyles.fontFamily,
    }),
  ];
  return paras;
};

export const rawData: ICitationData = {
  contributors: {
    [ContributorTypes.AUTHORS]: [
      {
        firstName: 'Atul',
        middleName: 'Kumar',
        lastName: 'Walia',
      },
      {
        firstName: 'Rajat',
        middleName: 'Big',
        lastName: 'L',
      },
      {
        firstName: 'Ravi',
        middleName: 'Big',
        lastName: 'Bore',
      },
    ],
    editors: [
      {
        firstName: 'Aniket',
        middleName: 'Tree',
        lastName: 'Vedi',
      },
      {
        firstName: 'Prem',
        middleName: 'Love',
        lastName: 'Guru',
      },
      {
        firstName: 'Atul',
        middleName: 'Chota',
        lastName: 'L',
      },
    ],
    translators: [
      {
        firstName: 'YO',
        middleName: 'YO',
        lastName: 'Honey',
      },
      {
        firstName: 'Kumar',
        lastName: 'Sanu',
      },
      {
        lastName: 'Khan',
      },
    ],
    compilers: [
      {
        firstName: 'Rajiv',
      },
      {
        middleName: 'Iyer',
      },
      {
        lastName: 'GOEL',
      },
    ],
    illustrators: [],
  },
  publicationInfo: {
    issue: 10,
    publishDate: {
      year: 2000,
      month: 'Jan',
      day: 10,
      time: '12:12:12',
    },
    accessDate: {
      year: 1900,
      month: 'Feb',
      day: 20,
    },
    title: 'Title for this work',
    websiteTitle: 'website title',
    subTitle: 'Subtitle for title',
    journalTitle: 'Journal title',
    citedPages: {
      start: 10,
      end: 20,
    },
    edition: '1',
    volume: '2',
    series: '3',
    totalVolumes: '10',
    publisher: 'publisher/sponsor',
    doi: 'doi',
    city: 'city',
    state: 'state',
    url: 'https://quillbot.com',
  },
  citationSource: CitationSourceEnum.JOURNAL,
};

export const exportAllCitations = (
  allCitations: any[],
  citationStyle: CitationStylesEnum | string
) => {
  const allFormattedCitations = convertToExportableFormat(allCitations);

  if (citationStyle === CitationStylesEnum.APA) {
    documentStyles.documentHeading = 'References';
    documentStyles.spacing = {
      belowHeading: 0,
      betweenCitations: 0,
      betweenLines: 480,
    };
  } else if (citationStyle === CitationStylesEnum.MLA) {
    documentStyles.documentHeading = 'Works Cited';
    documentStyles.spacing = {
      belowHeading: 0,
      betweenCitations: 0,
      betweenLines: 480,
    };
  } else if (citationStyle === CitationStylesEnum.CHICAGO) {
    documentStyles.documentHeading = 'Bibliography';
    documentStyles.spacing = {
      belowHeading: 1,
      betweenCitations: 0,
      betweenLines: 240,
    };
  }

  const doc = new CitationDocument({
    title: documentStyles.documentHeading,
    sections: [
      {
        children: [
          new CitationParagraph({
            spacing: {
              line: documentStyles.spacing.betweenLines,
            },
            children: [
              new CitationTextRun({
                text: documentStyles.documentHeading,
                size: 24,
                font: documentStyles.fontFamily,
              }),
              new CitationTextRun({
                text: '',
                size: 24,
                breakValue: documentStyles.spacing.belowHeading,
                font: documentStyles.fontFamily,
              }),
            ],
            alignment: AlignmentType.CENTER,
          }),
          ...allFormattedCitations.map((formattedCitation) =>
            generateCitation(formattedCitation, citationStyle)
          ),
        ],
      },
    ],
  });
  return doc;
};

export const getLastNameWithFallback = (contributor: ContributorDetails) =>
  capitalizeAllFirstLetters(
    contributor.lastName || contributor.firstName || contributor.middleName || ''
  );

export const generateChicagoInTextCitation = (citation: ICitationData, identifier?: string) => {
  const inTextcitationComponents: any = [];
  let inTextCitation = '(';
  inTextcitationComponents.push({
    text: '(',
    style: '',
    label: 'openingParanthesis',
  });

  const { contributors, publicationInfo, citationSource } = citation;
  const { authors, editors } = contributors;
  const { citedPages } = publicationInfo;
  const authorsAndEditorsMerged = [...authors, ...editors];

  if (authorsAndEditorsMerged.length === 1) {
    const author = `${getLastNameWithFallback(authorsAndEditorsMerged[0])}`;
    inTextCitation += author;
    inTextcitationComponents.push({ text: author, style: '', label: 'contributors' });
  } else if (authorsAndEditorsMerged.length === 2) {
    const author = `${getLastNameWithFallback(
      authorsAndEditorsMerged[0]
    )} and ${getLastNameWithFallback(authorsAndEditorsMerged[1])}`;
    inTextCitation += author;
    inTextcitationComponents.push({ text: author, style: '', label: 'contributors' });
  } else if (authorsAndEditorsMerged.length === 3) {
    const author = `${getLastNameWithFallback(
      authorsAndEditorsMerged[0]
    )}, ${getLastNameWithFallback(authorsAndEditorsMerged[1])} and ${getLastNameWithFallback(
      authorsAndEditorsMerged[2]
    )}
      }`;
    inTextCitation += author;
    inTextcitationComponents.push({ text: author, style: '', label: 'contributors' });
  } else if (authorsAndEditorsMerged.length >= 4) {
    const author = `${getLastNameWithFallback(authorsAndEditorsMerged[3])} et al.`;
    inTextCitation += author;
    inTextcitationComponents.push({ text: author, style: '', label: 'contributors' });
  }

  const isPublishYearAvailable = !!publicationInfo.publishDate?.year;
  const isAccessYearAvailable = !!publicationInfo.accessDate?.year;

  if (
    authorsAndEditorsMerged.length > 0 &&
    inTextcitationComponents.length > 1 &&
    (publicationInfo.title ||
      (citationSource === CitationSourceEnum.BOOK && isPublishYearAvailable) ||
      isAccessYearAvailable ||
      identifier)
  ) {
    inTextCitation += ' ';
    inTextcitationComponents[1].text = `${inTextcitationComponents[1].text} `;
  }

  if (publicationInfo.title) {
    inTextCitation += `${publicationInfo.title}`;
    inTextcitationComponents.push({
      text: `${publicationInfo.title}`,
      style: 'italic',
      label: 'title',
    });
  }

  if (citationSource === CitationSourceEnum.BOOK) {
    if (isPublishYearAvailable) {
      inTextCitation += ` ${publicationInfo.publishDate.year}`;
      inTextcitationComponents.push({
        text: ` ${publicationInfo.publishDate.year}`,
        style: '',
        label: 'year',
      });
    } else {
      inTextCitation += ' n.d.';
      inTextcitationComponents.push({ text: ' n.d.', style: '', label: 'year' });
    }
  }
  if (
    citationSource === CitationSourceEnum.JOURNAL ||
    citationSource === CitationSourceEnum.WEBSITE
  ) {
    if (isAccessYearAvailable) {
      inTextCitation += ` ${publicationInfo.accessDate.year}`;
      inTextcitationComponents.push({
        text: ` ${publicationInfo.accessDate.year}`,
        style: '',
        label: 'year',
      });
    } else if (isPublishYearAvailable) {
      inTextCitation += ` ${publicationInfo.publishDate.year}`;
      inTextcitationComponents.push({
        text: ` ${publicationInfo.publishDate.year}`,
        style: '',
        label: 'year',
      });
    } else {
      inTextCitation += ' n.d.';
      inTextcitationComponents.push({ text: ' n.d.', style: '', label: 'year' });
    }
  }
  if (citedPages?.start || citedPages?.end) {
    if (citedPages?.start && citedPages?.end) {
      inTextCitation += `${citedPages.start}–${citedPages.end}`;
      inTextcitationComponents.push({
        text: ` ${citedPages.start}–${citedPages.end}`,
        style: '',
        label: 'pages',
      });
    } else {
      const citePage = citedPages?.start ? ` ${citedPages?.start}` : ` ${citedPages?.end}`;
      inTextCitation += citePage;
      inTextcitationComponents.push({ text: `${citePage}`, style: '', label: 'pages' });
    }
  }
  if (identifier) {
    inTextCitation += ` ${identifier}`;
    inTextcitationComponents.push({ text: ` ${identifier}`, style: '', label: 'identifiers' });
  }

  inTextcitationComponents.push({
    text: ')',
    style: '',
    label: 'closingParanthesis',
  });
  inTextCitation += ')';
  return { inTextCitationString: inTextCitation, inTextcitationComponents };
};

export const generateMLAIntextCitation = (citation: ICitationData, identifier?: string) => {
  const inTextcitationComponents: any = [];
  let inTextCitation = '(';
  inTextcitationComponents.push({
    text: '(',
    style: '',
    label: 'openingParanthesis',
  });

  const { contributors, publicationInfo, citationSource } = citation;
  const { authors, editors } = contributors;
  const { citedPages } = publicationInfo;
  const authorsAndEditorsMerged = [...authors, ...editors];

  if (authorsAndEditorsMerged.length === 1) {
    const author = `${getLastNameWithFallback(authorsAndEditorsMerged[0])}`;
    inTextCitation += author;
    inTextcitationComponents.push({ text: author, style: '', label: 'contributors' });
  } else if (authorsAndEditorsMerged.length === 2) {
    const author = `${getLastNameWithFallback(
      authorsAndEditorsMerged[0]
    )} and ${getLastNameWithFallback(authorsAndEditorsMerged[1])}`;
    inTextCitation += author;
    inTextcitationComponents.push({ text: author, style: '', label: 'contributors' });
  } else if (authorsAndEditorsMerged.length >= 3) {
    const author = `${getLastNameWithFallback(authorsAndEditorsMerged[0])} et al.`;
    inTextCitation += author;
    inTextcitationComponents.push({ text: author, style: '', label: 'contributors' });
  }

  const isPublishYearAvailable = !!publicationInfo.publishDate?.year;
  const isAccessYearAvailable = !!publicationInfo.accessDate?.year;

  if (
    authorsAndEditorsMerged.length > 0 &&
    inTextcitationComponents.length > 1 &&
    (publicationInfo.title ||
      (citationSource === CitationSourceEnum.BOOK && isPublishYearAvailable) ||
      isAccessYearAvailable ||
      identifier)
  ) {
    inTextCitation += ' ';
    inTextcitationComponents[1].text = `${inTextcitationComponents[1].text} `;
  }

  if (publicationInfo.title) {
    inTextCitation += `${publicationInfo.title}`;
    inTextcitationComponents.push({
      text: `${publicationInfo.title}`,
      style: 'italic',
      label: 'title',
    });
  }
  if (identifier) {
    inTextCitation += ` ${identifier}`;
    inTextcitationComponents.push({ text: ` ${identifier}`, style: '', label: 'identifiers' });
  }
  inTextCitation += ')';
  inTextcitationComponents.push({
    text: ')',
    style: '',
    label: 'closingParanthesis',
  });
  // if (authorsAndEditorsMerged.length === 0) {
  //   return { inTextCitationString: '()', inTextcitationComponents };
  // }
  return { inTextCitationString: inTextCitation, inTextcitationComponents };
};

export const generateAPAInTextCitation = (citation: ICitationData, identifier?: string) => {
  const inTextcitationComponents: any = [];
  let inTextCitation = '(';
  inTextcitationComponents.push({
    text: '(',
    style: '',
    label: 'openingParanthesis',
  });

  const { contributors, publicationInfo, citationSource } = citation;
  const { authors, editors } = contributors;
  const { citedPages } = publicationInfo;
  const authorsAndEditorsMerged = [...authors, ...editors];

  if (authorsAndEditorsMerged.length === 1) {
    const author = `${getLastNameWithFallback(authorsAndEditorsMerged[0])}`;
    inTextCitation += author;
    inTextcitationComponents.push({ text: author, style: '', label: 'contributors' });
  } else if (authorsAndEditorsMerged.length === 2) {
    const author = `${getLastNameWithFallback(
      authorsAndEditorsMerged[0]
    )} & ${getLastNameWithFallback(authorsAndEditorsMerged[1])}`;
    inTextCitation += author;
    inTextcitationComponents.push({ text: author, style: '', label: 'contributors' });
  } else if (authorsAndEditorsMerged.length >= 3) {
    const author = `${getLastNameWithFallback(authorsAndEditorsMerged[0])} et al.`;
    inTextCitation += author;
    inTextcitationComponents.push({ text: author, style: '', label: 'contributors' });
  }

  const isPublishYearAvailable = !!publicationInfo.publishDate?.year;
  const isAccessYearAvailable = !!publicationInfo.accessDate?.year;

  if (
    authorsAndEditorsMerged.length > 0 &&
    inTextcitationComponents.length > 1 &&
    (publicationInfo.title ||
      (citationSource === CitationSourceEnum.BOOK && isPublishYearAvailable) ||
      isAccessYearAvailable ||
      identifier)
  ) {
    inTextCitation += ', ';
    inTextcitationComponents[1].text = `${inTextcitationComponents[1].text}, `;
  }

  if (publicationInfo.title) {
    inTextCitation += `${publicationInfo.title}`;
    inTextcitationComponents.push({
      text: `${publicationInfo.title}`,
      style: 'italic',
      label: 'title',
    });
  }

  if (citationSource === CitationSourceEnum.BOOK) {
    if (isPublishYearAvailable) {
      inTextCitation += ` ${publicationInfo.publishDate.year}`;
      inTextcitationComponents.push({
        text: ` ${publicationInfo.publishDate.year}`,
        style: '',
        label: 'year',
      });
    }
  }
  if (
    citationSource === CitationSourceEnum.JOURNAL ||
    citationSource === CitationSourceEnum.WEBSITE
  ) {
    if (isAccessYearAvailable) {
      inTextCitation += ` ${publicationInfo.accessDate.year}`;
      inTextcitationComponents.push({
        text: ` ${publicationInfo.accessDate.year}`,
        style: '',
        label: 'year',
      });
    } else if (isPublishYearAvailable) {
      inTextCitation += ` ${publicationInfo.publishDate.year}`;
      inTextcitationComponents.push({
        text: ` ${publicationInfo.publishDate.year}`,
        style: '',
        label: 'year',
      });
    }
  }
  if (identifier) {
    inTextCitation += ` ${identifier}`;
    inTextcitationComponents.push({ text: ` ${identifier}`, style: '', label: 'identifiers' });
  }
  inTextcitationComponents.push({
    text: ')',
    style: '',
    label: 'closingParanthesis',
  });
  inTextCitation += ')';
  return { inTextCitationString: inTextCitation, inTextcitationComponents };
};

export const generateInTextCitation = (
  citation: any,
  citationStyle: CitationStylesEnum | string,
  identifier?: string
) => {
  const formattedCitation = convertToExportableFormat([citation])[0];
  if (citationStyle === CitationStylesEnum.APA) {
    return generateAPAInTextCitation(formattedCitation, identifier);
  }
  if (citationStyle === CitationStylesEnum.CHICAGO) {
    return generateChicagoInTextCitation(formattedCitation, identifier);
  }
  if (citationStyle === CitationStylesEnum.MLA) {
    return generateMLAIntextCitation(formattedCitation, identifier);
  }
  return { inTextCitationString: '', inTextcitationComponents: [] };
};
*/

export const citationListToRender = Object.values(CitationStylesEnum);
export const citationStyleFullForms = {
  APA: 'APA (American Psychological Association)',
  MLA: 'MLA (Modern Language Association)',
  CHICAGO: 'Chicago (Chicago Manual of Style)',
};

export const convertCitedDataResponse = (
  citedDataResponse?: GenerateCitationsResponse
): CitedData[] =>
  citedDataResponse?.data.map((d) => ({
    id: d.id,
    short: d.shortCitation,
    full: d.fullCitation,
    json: d.json,
    bibtex: d.bibtex,
  })) || [];

export const downloadFileFromBlob = (blob: Blob, fileName: string) => {
  try {
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      return window.navigator.msSaveOrOpenBlob(blob);
    }

    const data = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = data;
    link.download = fileName;

    // this is necessary as link.click() does not work on the latest firefox
    link.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      })
    );

    setTimeout(() => {
      // For Firefox it is necessary to delay revoking the ObjectURL
      window.URL.revokeObjectURL(data);
      link.remove();
    }, 100);

    return true;
  } catch (e) {
    openToastr('Sorry! File export failed!');
    logger('Error in exporting file', e);

    return false;
  }
};

export const downloadFile = (content: string | Blob, fileName: string, mimeType?: string) => {
  if (content instanceof Blob) {
    return downloadFileFromBlob(content, fileName);
  }

  const blobOptions: BlobPropertyBag = {};
  if (mimeType) {
    blobOptions.type = mimeType;
  }
  const blob = new Blob([content], blobOptions);

  return downloadFileFromBlob(blob, fileName);
};

const apaRegex = /\bapa\b/i;
const mlaRegex = /\bmodern-language-association\b/i;
export const getBibSectionTitle = (citationStyleId: string) => {
  if (mlaRegex.test(citationStyleId)) {
    return 'Works Cited';
  }

  if (apaRegex.test(citationStyleId)) {
    return 'References';
  }

  return 'Bibliography';
};

const re1 = /^1|\[1\]|\(1\)$/;
const re2 = /^1[^A-Za-z]\.?\s?|\[1\]|\(1\)/;
export const handleCitationNumberOnText = (text: string, citationNumber?: number) => {
  if (
    re1.test(text) || // text matches format of short citation
    re2.test(text) // text matches format of full citation
  ) {
    return text.replace('1', (citationNumber ?? 1).toString());
  }

  return text;
};

export const exportToDocx = (citations: CitedData[], citationStyle: string) => {
  documentStyles.documentHeading = getBibSectionTitle(citationStyle);

  const doc = new CitationDocument({
    title: documentStyles.documentHeading,
    sections: [
      {
        children: [
          new CitationParagraph({
            spacing: {
              line: documentStyles.spacing.betweenLines,
            },
            children: [
              new CitationTextRun({
                text: documentStyles.documentHeading,
                size: documentStyles.fontSize,
                font: documentStyles.fontFamily,
              }),
              new CitationTextRun({
                text: '',
                size: documentStyles.fontSize,
                breakValue: documentStyles.spacing.belowHeading,
                font: documentStyles.fontFamily,
              }),
            ],
            alignment: AlignmentType.CENTER,
          }),
          ...citations.map(
            (citation) =>
              new CitationParagraph({
                indent: { start: 1000, hanging: 1000 },
                spacing: {
                  line: documentStyles.spacing.betweenLines,
                },
                children: citation.full.map(
                  (citationPart, index) =>
                    new CitationTextRun({
                      text:
                        index === 0
                          ? handleCitationNumberOnText(citationPart.text, citation.citationNumber)
                          : citationPart.text,
                      font: documentStyles.fontFamily,
                      size: documentStyles.fontSize,
                      italics: citationPart.style?.i,
                      bold: citationPart.style?.b,
                      subScript: citationPart.style?.sub,
                      superScript: citationPart.style?.sup,
                    })
                ),
              })
          ),
        ],
      },
    ],
  });

  return doc;
};

export const exportToCslJson = (citations: CitedData[]) =>
  JSON.stringify(
    citations.map((citation) => citation.json),
    undefined,
    2
  );

export const exportToBibtex = (citations: CitedData[]) =>
  citations.map((citation) => citation.bibtex).join('\n');

export const getCitationStyleLocalStorageKey = () =>
  `citationStyle${getReduxStore()?.getState()?.common?.partnerCompany}`;

export const getCitationStyleFromLocalStorage = () => {
  const LocalStorage = QuillLocalStorage.getInstance();
  let citationStyle = LocalStorage.get(
    getCitationStyleLocalStorageKey(),
    'modern-language-association'
  );

  switch (citationStyle) {
    case CitationStylesEnum.APA:
      citationStyle = 'apa';
      break;
    case CitationStylesEnum.CHICAGO:
      citationStyle = 'chicago-note-bibliography';
      break;
    case CitationStylesEnum.MLA:
      citationStyle = 'modern-language-association';
      break;
    default:
      break;
  }

  return citationStyle || 'modern-language-association';
};

const GET_CITATION_STYLES_MAX_TRIES = 3;
export const useCitationStyles = () => {
  const dispatch = useDispatch();
  const { citationStyle, citationStyles } = useSelector((state: RootState) => state.ctgr);
  const isSpamCheckCallCompleted = useSelector(
    (state: RootState) => state.common.spamCheckCallCompleted
  );
  const isSyncWithDB = useSelector((state: RootState) => state?.user?.syncedWithDb || false);

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [isLoading, _setIsLoading] = useState(false);
  const setIsLoading = useSafeDispatch(_setIsLoading);

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [triesLeft, _setTriesLeft] = useState(GET_CITATION_STYLES_MAX_TRIES);
  const setTriesLeft = useSafeDispatch(_setTriesLeft);

  useEffect(() => {
    if ((isSyncWithDB || isSpamCheckCallCompleted) && triesLeft > 0 && !citationStyles.length) {
      setIsLoading(true);
      getCitationStyles({})
        .then((data) => {
          dispatch(setCitationStyles(data.styles));
        })
        .catch((e) => {
          openToastr('Unable to retrieve citation styles!');
        })
        .finally(() => {
          setIsLoading(false);
          setTriesLeft((tries) => tries - 1);
        });
    } else if (!isSyncWithDB) {
      setIsLoading(true);
    }
  }, [isSpamCheckCallCompleted, citationStyle, citationStyles, isSyncWithDB]);

  return [citationStyles, isLoading] as [citationStyles: ICitationStyle[], isLoading: boolean];
};

export const scrollIntoViewCitationCard = (refId: string, delay = 1) => {
  setTimeout(() => {
    const node = document.querySelector(`[data-citation-card-ref='${refId}']`);
    if (node) {
      node?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }, delay);
};

export const unitPX = (value: any) => `${parseFloat(value)}px`;
export const getCgBreakpointUp = (theme: Theme) => theme.breakpoints.up('md');
export const getCgBreakpointDown = (theme: Theme) => theme.breakpoints.down('md');

const DOI_REGEXP = new RegExp('10.\\d{4,9}\\/[-._;()/:A-Z0-9]+$', 'i');
export const isDoi = (s: string) => DOI_REGEXP.test(s);

export const getSearchPlaceholder = (sourceType: CitationSourceEnum) => {
  switch (sourceType) {
    case CitationSourceEnum.BOOK:
      return 'Search for Books';
    case CitationSourceEnum.JOURNAL:
      return 'Search for Journal Articles';
    case CitationSourceEnum.WEBSITE:
      return 'Enter URL';
    default:
      return 'Search by keywords';
  }
};

const rejectTags = ['b', 'i', 'sup', 'sub'].map((tag) => `<${tag}>|</${tag}>`).join('|');
const sanitizerFns: Array<(s: string) => string> = [
  (str) => str.trim(),
  (str) => str.replace(new RegExp(rejectTags, 'gi'), ''),
  (str) =>
    str
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"'),
];
export const sanitizeText = (input = '') =>
  input && typeof input === 'string'
    ? sanitizerFns.reduce((out, sanitizerFn) => sanitizerFn(out), input)
    : '';

/** Top APA style ids */
export const topApaStyleIds = ['apa', 'apa-6th-edition'];
/** Top MLA style ids */
export const topMlaStyleIds = [
  'modern-language-association',
  'modern-language-association-8th-edition',
];
/** Top CHICAGO style ids */
export const topChicagoStyleIds = ['chicago-note-bibliography'];
